import * as React from 'react'
import {
  Autocomplete as MuiAutocomplete,
  AutocompleteFreeSoloValueMapping,
  AutocompleteProps as MuiAutocompleteProps,
  TextField,
  TextFieldProps,
  AutocompleteValue,
  useTheme,
  styled,
  Popper,
  useMediaQuery,
  autocompleteClasses,
} from '@mui/material'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'
import { VariableSizeList, ListChildComponentProps } from 'react-window'

const LISTBOX_PADDING = 8

export type Option = {
  label: string
  value: string
  additional_fields?: Record<string, unknown>
}

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
})

const OuterElementContext = React.createContext({})

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext)
  return <div ref={ref} {...props} {...outerProps} />
})

function renderRow(props: ListChildComponentProps<React.ReactElement[]>) {
  const { data, index, style } = props
  const dataSet = data[index] as React.ReactElement<React.LiHTMLAttributes<HTMLLIElement>, 'li'>

  if (!React.isValidElement(dataSet)) {
    return null
  }

  const inlineStyle: React.CSSProperties = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  }

  return React.cloneElement(dataSet, {
    style: inlineStyle,
    key: dataSet.key ?? index,
  })
}

function useResetCache(data: unknown) {
  const ref = React.useRef<VariableSizeList>(null)
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true)
    }
  }, [data])
  return ref
}

const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  function ListboxComponent(props, ref) {
    const { children, ...other } = props
    const itemData: React.ReactElement[] = []
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child)) {
        itemData.push(child)
      }
    })

    const theme = useTheme()
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
      noSsr: true,
    })

    const itemCount = itemData.length
    const itemSize = smUp ? 36 : 48

    const getHeight = () => {
      if (itemCount > 8) {
        return 8 * itemSize
      }
      return itemCount * itemSize
    }

    const gridRef = useResetCache(itemCount)

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <VariableSizeList
            itemData={itemData}
            height={getHeight() + 2 * LISTBOX_PADDING}
            width="100%"
            ref={gridRef}
            outerElementType={OuterElementType}
            innerElementType="ul"
            itemSize={() => itemSize}
            overscanCount={5}
            itemCount={itemCount}
          >
            {renderRow}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    )
  },
)

export default function VirtualizedDropdown<
  T extends Option,
  P extends FieldValues,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>({
  control,
  name,
  defaultValue,
  rules,
  label,
  placeholder,
  options,
  slotProps,
  onBlur,
  multiple,
  ...rest
}: Omit<MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'renderInput'> &
  Omit<TextFieldProps, 'error' | 'onChange' | 'required'> &
  UseControllerProps<P>) {
  const {
    field: { ref, onBlur: onFieldBlur, onChange: onFieldChange, value, ...field },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
    defaultValue,
  })

  function isOption(param: unknown): param is Option {
    return Boolean((param as Option)?.value !== undefined && (param as Option)?.label !== undefined)
  }

  function getOptionLabel(option: T | AutocompleteFreeSoloValueMapping<FreeSolo>) {
    return isOption(option) ? option.label : option.toString()
  }

  function getOptionValue(option: T | AutocompleteFreeSoloValueMapping<FreeSolo>) {
    return isOption(option) ? option.value.toString() : option.toString()
  }
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const selectedOption: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo> = multiple
    ? options.filter((option) => (value as string[])?.includes(option.value.toString()))
    : options.find((option) => option.value.toString() === value?.toString()) || (rest.freeSolo ? (value as any) : null)

  return (
    <MuiAutocomplete
      data-testid="virtualized-dropdown-autocomplete"
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, value) => getOptionValue(option) === getOptionValue(value)}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          label={label}
          placeholder={placeholder}
          error={!!error}
          helperText={error && error.message ? error.message : null}
          inputRef={ref}
          slotProps={{
            input: {
              ...(slotProps?.input || {}),
              ...params.InputProps,
            },
          }}
        />
      )}
      onChange={(_, value) => {
        const optionValue = multiple
          ? (value as T[]).map((option) => (isOption(option) ? option.value.toString() : option))
          : isOption(value)
            ? value.value.toString()
            : value
        onFieldChange(optionValue)
      }}
      fullWidth
      options={options}
      value={selectedOption}
      onBlur={(event) => {
        onFieldBlur()
        if (onBlur) {
          onBlur(event)
        }
      }}
      multiple={multiple}
      disableListWrap
      slots={{
        popper: StyledPopper,
        listbox: ListboxComponent,
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.value}>
          {option.label}
        </li>
      )}
      {...field}
      {...rest}
    />
  )
}
